// Job submission form component

import { useState, useCallback, type FormEvent } from "react";
import type { CreateJobResponse, ErrorResponse } from "@dishsnap/shared";
import styles from "./JobForm.module.css";
import { apiUrl } from "../config";

interface JobFormProps {
  onJobCreated: (jobId: string) => void;
  disabled?: boolean;
}

// TikTok URL validation pattern
const TIKTOK_URL_PATTERN = /^https?:\/\/(www\.)?(tiktok\.com|vm\.tiktok\.com|vt\.tiktok\.com)/i;

export function JobForm({ onJobCreated, disabled = false }: JobFormProps) {
  const [url, setUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateUrl = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return "Please enter a TikTok URL";
    }
    if (!TIKTOK_URL_PATTERN.test(value.trim())) {
      return "Please enter a valid TikTok URL";
    }
    return null;
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const validationError = validateUrl(url);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(apiUrl("/api/jobs"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim() }),
      });

      const data = await response.json() as CreateJobResponse | ErrorResponse;

      if (!response.ok) {
        const errorData = data as ErrorResponse;
        throw new Error(errorData.message || "Failed to create job");
      }

      const successData = data as CreateJobResponse;
      setUrl("");
      onJobCreated(successData.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  }, [url, validateUrl, onJobCreated]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (error) setError(null);
  }, [error]);

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.inputGroup}>
        <label htmlFor="url-input" className={styles.label}>
          TikTok Recipe URL
        </label>
        <div className={styles.inputWrapper}>
          <input
            id="url-input"
            type="url"
            value={url}
            onChange={handleInputChange}
            placeholder="https://www.tiktok.com/@chef/video/..."
            className={styles.input}
            disabled={disabled || isSubmitting}
            aria-describedby={error ? "url-error" : undefined}
            aria-invalid={error ? "true" : undefined}
          />
          <button
            type="submit"
            className={styles.button}
            disabled={disabled || isSubmitting || !url.trim()}
          >
            {isSubmitting ? (
              <span className={styles.spinner} aria-label="Submitting..." />
            ) : (
              "Extract Dish"
            )}
          </button>
        </div>
        {error && (
          <p id="url-error" className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
      <p className={styles.hint}>
        Paste a TikTok recipe video link to extract the best finished dish frame
      </p>
    </form>
  );
}
